import { axiosInstance } from "@/config/axiosInstance";
import type { BreakView, DaySummary } from "@/components/break/types";
import { getDeviceId } from "@/lib/device-id";

// ? Login Employee Action
export const loginEmployee = (data: { email: string; password: string }) =>
  axiosInstance
    .post<{
      success: boolean;
      error?: string;
      response: {
        orderId: string;
      };
      key: string;
      employee: {
        _id: string;
        phone: string;
      };
    }>("/employee/login", data, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((response) => response.data);

// ? Employee Checkout Action
export const checkoutEmployee =
  (employeeKey: string) =>
  (data: {
    employee: string;
    workDoneToday: string;
    attachments?: {
      doc: string;
      url: string;
    }[];
    ip: string;
    device: string;
    os: string;
    screenSize: string;
    browser: string;
  }) =>
    axiosInstance
      .post<{
        success: true;
        summary?: DaySummary | null;
      }>("/employee/checkout", { ...data, deviceId: getDeviceId() }, {
        headers: {
          authorization: `Bearer ${employeeKey}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => response.data);

// ? Create Employee Activity Action
export const createEmployeeActivity =
  () =>
  (data: {
    employee: string;
    mode: string;
    ip: string;
    device: string;
    os: string;
    screenSize: string;
    browser: string;
  }) =>
    axiosInstance
      .post(`/employee/activity`, { ...data, deviceId: getDeviceId() }, {
        headers: {
          // authorization: `Bearer ${adminKey}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => response.data);

// ? Employee Checkin Action
export const checkinEmployee = (data: {
  token: string;
  employee: string;
  ip: string;
  device: string;
  os: string;
  screenSize: string;
  browser: string;
}) =>
  axiosInstance
    .post(
      "/employee/checkin",
      {
        employee: data.employee,
        ip: data.ip,
        device: data.device,
        os: data.os,
        screenSize: data.screenSize,
        browser: data.browser,
        deviceId: getDeviceId(),
      },
      {
        headers: {
          authorization: `Bearer ${data.token}`,
          "Content-Type": "application/json",
        },
      }
    )
    .then((response) => response.data);

// ? Create Leave Application Action

export const createLeaveApplicationForEmployee =
  (employeeKey: string) =>
  (data: {
    leaveType: string;
    startDate: Date;
    endDate: Date;
    reason: string;
    isPaid: boolean;
    attachment?: string | undefined;
    totalDays: number;
    isHalfDay: boolean;
  }) =>
    axiosInstance
      .post(`/leave/create`, data, {
        headers: {
          authorization: `Bearer ${employeeKey}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => response.data);

// ? Edit Leave Application Action

export const editLeaveApplicationForEmployee =
  (employeeKey: string) =>
  (data: {
    leaveId: string;
    info: {
      leaveType: string;
      startDate: Date;
      endDate: Date;
      reason: string;
      isPaid: boolean;
      attachment?: string | undefined;
      totalDays: number;
    };
  }) =>
    axiosInstance
      .post(`/leave/edit/${data.leaveId}`, data.info, {
        headers: {
          authorization: `Bearer ${employeeKey}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => response.data);

// ? Create New Allowance Action
export const createNewAllowanceRequest =
  (employeeKey: string) =>
  (data: { reason: string; amountRequested: number; attachment?: string }) =>
    axiosInstance
      .post(`/allowance/create`, data, {
        headers: {
          authorization: `Bearer ${employeeKey}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => response.data);

// ? Cancel Leave Application Action
export const cancelLeaveApplication =
  (adminKey: string) => (data: { leaveId: string }) =>
    axiosInstance
      .post(
        `/leave/cancel/${data.leaveId}`,
        {},
        {
          headers: {
            authorization: `Bearer ${adminKey}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => response.data);

// ? Cancel Allowance Request Action
export const cancelAllowanceRequest =
  (adminKey: string) => (data: { allowanceId: string }) =>
    axiosInstance
      .post(
        `/allowance/cancel/${data.allowanceId}`,
        {},
        {
          headers: {
            authorization: `Bearer ${adminKey}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => response.data);

// ? Create New Loan Request Action
export const createNewLoanRequest =
  (employeeKey: string) =>
  (data: { totalAmountRequested: number; attachment?: string }) =>
    axiosInstance
      .post(`/loan/create`, data, {
        headers: {
          authorization: `Bearer ${employeeKey}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => response.data);

// ? Create New Loan Repayment Request Action
export const createNewLoanRepaymentRequest =
  (employeeKey: string) =>
  (data: { amount: number; attachment?: string; loanId: string }) =>
    axiosInstance
      .post(`/loan/create/request`, data, {
        headers: {
          authorization: `Bearer ${employeeKey}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => response.data);

// ? Create New Attendance Request Action
export const createNewAttendanceRequest =
  (employeeKey: string) => (data: { date: Date; id: string; notes: string }) =>
    axiosInstance
      .post(`/attendance/request/create`, data, {
        headers: {
          authorization: `Bearer ${employeeKey}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => response.data);

// ? Mark absent Request Action
export const markAbsentAttendance = (employeeKey: string) => (attId: string) =>
  axiosInstance
    .post(
      `/attendance/employee/mark-absent/${attId}`,
      {},
      {
        headers: {
          authorization: `Bearer ${employeeKey}`,
          "Content-Type": "application/json",
        },
      }
    )
    .then((response) => response.data);

// ? Change Status of Attendance (requires reason and acknowledgment)
export const changeAttendanceStatusByAdmin =
  (adminkey: string) =>
  (data: {
    id: string;
    status: string;
    reason: string;
    acknowledgedChange: boolean;
  }) =>
    axiosInstance
      .post<{
        success: boolean;
        message?: string;
        attendance?: any;
        log?: any;
        error?: string;
      }>(`/attendance/change-status`, data, {
        headers: {
          authorization: `Bearer ${adminkey}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => response.data);

// ? Change Status of Attendance
export const addAttendanceCommentByAdmin =
  (adminkey: string) =>
  (data: {
    id: string;
    adminComment: {
      time: Date | string;
      comment: string;
    }[];
  }) =>
    axiosInstance
      .post(`/attendance/add-comment`, data, {
        headers: {
          authorization: `Bearer ${adminkey}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => response.data);

// ? Change Status of Attendance
export const addAttendanceCommmentByNewWay =
  (adminkey: string) =>
  (data: { employee: string; adminComment: string[]; date: string | Date }) =>
    axiosInstance
      .post(`/attendance/add-comment-model`, data, {
        headers: {
          authorization: `Bearer ${adminkey}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => response.data);

// ? Start a break (the employee id comes from the token)
export const startBreak = (employeeKey: string) => (data: { reason?: string }) =>
  axiosInstance
    .post<{ success: boolean; break: BreakView }>("/break/start", data, {
      headers: {
        authorization: `Bearer ${employeeKey}`,
        "Content-Type": "application/json",
      },
    })
    .then((response) => response.data);

// ? End the current break
export const endBreak = (employeeKey: string) => () =>
  axiosInstance
    .post<{ success: boolean; break: BreakView }>(
      "/break/end",
      {},
      {
        headers: {
          authorization: `Bearer ${employeeKey}`,
          "Content-Type": "application/json",
        },
      }
    )
    .then((response) => response.data);
